/**
 * Form Field Error Mappings
 *
 * Maps API error codes to specific form fields.
 * This is business logic: "when this error occurs, which field should show it?"
 *
 * ✅ Domain Layer - Pure mappings, no implementation details
 *
 * @module domains/personnel/domain/errors/formFieldMappings
 */

import { ERROR_CODES } from './errorCodes';

/**
 * Configuration for which errors should be mapped to form fields
 */
export interface FormFieldMapping {
  /** API error code to match */
  errorCode: string;
  /** Form field name to attach the error to */
  fieldName: string;
  /**
   * Fixed front-end copy for the field, as a namespaced i18n key. Without it the field shows the
   * backend message, which is not translated and reads like a log line.
   */
  messageKey?: string;
}

/**
 * Form field error metadata
 */
export interface FormFieldError {
  /** Field name that should show the error */
  field: string;
  /** Error code from the API */
  code: string;
}

/**
 * Mappings for role-related form field errors
 *
 * Defines which API errors should be shown on specific form fields
 * instead of displaying a toast notification.
 */
export const ROLE_FORM_FIELD_MAPPINGS: FormFieldMapping[] = [
  {
    errorCode: ERROR_CODES.ROLES.ROLE_ALREADY_EXISTS,
    fieldName: 'name',
  },
  // Add more role field mappings as needed
  // {
  //   errorCode: ERROR_CODES.ROLES.INVALID_PERMISSIONS,
  //   fieldName: 'permissions',
  // },
];

/**
 * Mappings for employee-related form field errors
 *
 * Defines which API errors should be shown on specific form fields
 * for employee forms.
 */
export const EMPLOYEE_FORM_FIELD_MAPPINGS: FormFieldMapping[] = [
  {
    errorCode: ERROR_CODES.EMPLOYEES.EMAIL_ALREADY_IN_USE,
    fieldName: 'email',
    messageKey: 'personnel-employees:form.errors.emailAlreadyInUse',
  },
  {
    errorCode: ERROR_CODES.EMPLOYEES.PIN_ALREADY_IN_USE,
    fieldName: 'posPin',
  },
];

/**
 * Checks if an error code should be handled by a form field
 *
 * This is a pure function that encapsulates the business rule:
 * "some errors are field-specific and shouldn't show toasts"
 *
 * @param errorCode - The error code to check
 * @param mappings - Form field mappings to check against
 * @returns True if error should be handled by form, false if should show toast
 *
 * @example
 * if (shouldHandleInForm('roles.RoleAlreadyExists', ROLE_FORM_FIELD_MAPPINGS)) {
 *   // Set error on form field instead of showing toast
 * }
 */
export const shouldHandleInForm = (errorCode: string, mappings: FormFieldMapping[]): boolean => {
  return mappings.some((mapping) => mapping.errorCode === errorCode);
};

/**
 * Gets the form field name for a given error code
 *
 * Pure function that finds the field mapping for an error code.
 *
 * @param errorCode - The error code from the API
 * @param mappings - Form field mappings to search in
 * @returns Form field name or undefined if no mapping exists
 *
 * @example
 * const fieldName = getFormFieldForError('roles.RoleAlreadyExists', ROLE_FORM_FIELD_MAPPINGS);
 * // Returns: 'name'
 */
export const getFormFieldForError = (errorCode: string, mappings: FormFieldMapping[]): string | undefined => {
  const mapping = mappings.find((m) => m.errorCode === errorCode);
  return mapping?.fieldName;
};

/**
 * Gets the fixed front-end copy for a given error code, when the mapping declares one.
 *
 * @param errorCode - The error code from the API
 * @param mappings - Form field mappings to search in
 * @returns Namespaced i18n key, or undefined when the backend message should be shown
 */
export const getFormMessageKeyForError = (errorCode: string, mappings: FormFieldMapping[]): string | undefined => {
  const mapping = mappings.find((m) => m.errorCode === errorCode);
  return mapping?.messageKey;
};
